import { useAuth } from "@features/auth";
import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useNavigation } from "@core/context/NavigationContext";
import { Info, InfoIcon, RotateCcw } from "lucide-react";
import React from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@shared/ui/breadcrumb";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { useQueryClient } from "@tanstack/react-query";

export function useBaseComponent() {
  const [error, setError] = useState<Error | undefined>(undefined);
  const { user } = useAuth();
  const location = useLocation();
  const { navigationItems } = useNavigation();
  const platformUrl = import.meta.env.VITE_PLATFORM_URL;
  const urlParts = platformUrl?.split("//")[1]?.split(".") || [];
  const environment = urlParts[0]?.replace("platform-", "") || "production";
  // Function to get environment styles and label
  const getEnvironmentConfig = (env: string) => {
    switch (env) {
      case "sandbox":
        return {
          label:
            "You are in a sandbox environment designed for practice and learning. No real operational or production actions will take place here.",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-300",
          textColor: "text-yellow-700",
          iconColor: "text-yellow-500",
          show: true,
        };
      case "dev":
      case "development":
        return {
          label: "This is Development Environment",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-300",
          textColor: "text-blue-700",
          iconColor: "text-blue-500",
          show: true,
        };
      case "staging":
        return {
          label: "This is Staging Environment",
          bgColor: "bg-orange-50",
          borderColor: "border-orange-300",
          textColor: "text-orange-700",
          iconColor: "text-orange-500",
          show: true,
        };
      case "new":
        return {
          label: "This is Pre-production Environment",
          bgColor: "bg-purple-50",
          borderColor: "border-purple-300",
          textColor: "text-purple-700",
          iconColor: "text-purple-500",
          show: true,
        };
      case "production":
      default:
        return {
          label: "This is Production Environment",
          bgColor: "bg-green-50",
          borderColor: "border-green-300",
          textColor: "text-green-700",
          iconColor: "text-green-500",
          show: false,
        };
    }
  };
  const envConfig = getEnvironmentConfig(environment);

  console.log(
    "Navigation Items in useBaseComponent:",
    JSON.stringify(navigationItems, null, 2),
  );

  function renderError() {
    if (!error) return null;
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <h2 className="text-lg font-semibold text-red-800">Error</h2>
        <p className="mt-2 text-sm text-red-600">{error.message}</p>
      </div>
    );
  }

  function formatSegment(segment: string): string {
    // Replace hyphens with spaces → "user-profile" → "user profile"
    const withSpaces = segment.replace(/-/g, " ");
    // Capitalize each word → "user profile" → "User Profile"
    return withSpaces.replace(/\b\w/g, (char) => char?.toUpperCase());
  }

  const queryClient = useQueryClient();

  // State to track last updated time
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const handleRefresh = () => {
    // Invalidate everything (triggers refetch of all active queries)
    queryClient.invalidateQueries();
    // Update last updated time
    setLastUpdated(new Date());
  };

  function normalize(path: string) {
    return path.replace(/\/+$/, ""); // remove trailing slash
  }

  function findClosestNavMatch(path: string) {
    let closestMatch: any = null;
    const cleanPath = normalize(path);
    const pathSegments = cleanPath.split("/").filter(Boolean);
    if (pathSegments.length === 0) return null;

    for (const group of navigationItems) {
      for (const item of group.items) {
        const itemPath = normalize(item.link);
        const itemSegments = itemPath.split("/").filter(Boolean);

        // Filter: Must be in the same root feature (e.g. /plan-tarrif or /utility)
        if (itemSegments[0] !== pathSegments[0]) {
          continue;
        }

        // Match: if one is a prefix of the other (e.g. /plan-tarrif matching /plan-tarrif/landing)
        if (cleanPath.startsWith(itemPath) || itemPath.startsWith(cleanPath)) {
          if (
            !closestMatch ||
            itemPath.length > closestMatch.item.link.length
          ) {
            closestMatch = { item, groupLabel: group.label, group };
          }
        }
      }
    }

    return closestMatch;
  }

  // function renderBreadcrumbs() {
  //   const path = location.pathname.split("?")[0];
  //   const segments = path.split("/").filter(Boolean);
  //   console.log("Current Path Segments:", segments);
  //   const moduleName =  localStorage.getItem('smart360_current_module'); // e.g., "cx", "mx"
  //   const fullPath = `/${segments.join("/")}`;

  //   const match = findClosestNavMatch(fullPath);
  //   console.log("Closest Navigation Match:", match);
  //   const matchedItem = match?.item || null;
  //   const matchedGroup = match?.group || null;

  //   // --------------------------------------------
  //   // Detect Dynamic ID (supports alphanumeric)
  //   // --------------------------------------------
  //   const lastSegment = segments[segments.length - 1];
  //   console.log("Last Path Segment for ID Detection:", segments);

  //   // Check if last segment exists in any menu path → then it's NOT an ID
  //   const isInMenu = navigationItems.some((group) =>
  //     group.items.some((item) =>
  //       normalize(item.link).split("/").includes(lastSegment)
  //     )
  //   );

  //   // Detect ID if not in menu & segment is alphanumeric/hyphens
  //   const isDynamicId = !isInMenu && /^[A-Za-z0-9-_]+$/.test(lastSegment);
  //   const dynamicId = isDynamicId ? lastSegment : null;

  //   // --------------------------------------------
  //   // Breadcrumb Build
  //   // --------------------------------------------
  //   const breadcrumbs: JSX.Element[] = [];

  //   // ==========================================================
  //   // 1️⃣ MODULE → Always clickable
  //   // Example: MX → /mx/dashboard
  //   // ==========================================================
  //   const firstModuleRoute = navigationItems[0]?.items?.[0]?.link;
  //   breadcrumbs.push(
  //     <BreadcrumbItem key="module">
  //       <BreadcrumbLink asChild>
  //         <Link to={firstModuleRoute}>{moduleName?.toUpperCase()}</Link>
  //       </BreadcrumbLink>
  //     </BreadcrumbItem>
  //   );

  //   if (matchedGroup || matchedItem) {
  //     breadcrumbs.push(<BreadcrumbSeparator key="sep-module" />);
  //   }

  //   // ==========================================================
  //   // 2️⃣ GROUP → e.g. DEVICE MANAGEMENT / MANAGEMENT / SETTINGS
  //   // ==========================================================
  //   if (matchedGroup) {
  //     const firstGroupItem = matchedGroup.items[0];
  //     const groupLink = firstGroupItem.link;

  //     breadcrumbs.push(
  //       <BreadcrumbItem key="group">
  //         <BreadcrumbLink asChild>
  //           <Link to={groupLink}>{matchedGroup.label}</Link>
  //         </BreadcrumbLink>
  //       </BreadcrumbItem>
  //     );

  //     if (matchedItem || dynamicId) {
  //       breadcrumbs.push(<BreadcrumbSeparator key="sep-group" />);
  //     }
  //   }

  //   // ==========================================================
  //   // 3️⃣ COMPONENT (e.g. Meter, Accounts, Complaints etc.)
  //   // If ID exists → component becomes clickable
  //   // ==========================================================
  //   if (matchedItem) {
  //     const componentLabel = matchedItem.label;

  //     breadcrumbs.push(
  //       <BreadcrumbItem key="component">
  //         {dynamicId ? (
  //           <BreadcrumbLink asChild>
  //             <Link to={matchedItem.link}>{componentLabel}</Link>
  //           </BreadcrumbLink>
  //         ) : (
  //           <BreadcrumbPage>{componentLabel}</BreadcrumbPage>
  //         )}
  //       </BreadcrumbItem>
  //     );

  //     if (dynamicId) {
  //       breadcrumbs.push(<BreadcrumbSeparator key="sep-component" />);
  //     }
  //   }

  //   // ==========================================================
  //   // 4️⃣ ID Segment (#CRN5, #220586 etc.)
  //   // ==========================================================
  //   if (dynamicId) {
  //     breadcrumbs.push(
  //       <BreadcrumbItem key="id">
  //         <BreadcrumbPage>#{dynamicId}</BreadcrumbPage>
  //       </BreadcrumbItem>
  //     );
  //   }

  //   // ==========================================================
  //   // FINAL RENDER WITH REFRESH FUNCTIONALITY
  //   // ==========================================================
  //   return (
  //     <div>
  //     {envConfig.show && (
  //         <div
  //           className={`flex items-center gap-2 border ${envConfig.borderColor} ${envConfig.bgColor} p-2 rounded`}
  //         >
  //           <InfoIcon className={`w-4 h-4 ${envConfig.iconColor}`} />
  //           <span className={envConfig.textColor}>{envConfig.label}</span>
  //         </div>
  //       )}

  //     <div className="flex items-center justify-between w-full">
  //       {/* Left: Breadcrumbs */}
  //       <Breadcrumb>
  //         <BreadcrumbList>{breadcrumbs}</BreadcrumbList>
  //       </Breadcrumb>

  //       {/* Right: Refresh + Info + Last Updated */}
  //       <div className="flex flex-col items-end gap-2">
  //         {/* Line 1 - Refresh button */}
  //         <button
  //           onClick={handleRefresh}
  //           className="flex items-center gap-2 px-2 py-2 mt-2 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
  //         >
  //           <RotateCcw className="w-4 h-4" />
  //         </button>

  //         {/* Line 2 - Info + last updated */}
  //         <div className="flex items-center gap-1 text-gray-600 text-xs">
  //           <TooltipProvider>
  //             <Tooltip>
  //               <TooltipTrigger asChild>
  //                 <Info className="w-4 h-4 cursor-pointer hover:text-gray-800 transition-colors" />
  //               </TooltipTrigger>
  //               <TooltipContent>
  //                 <p>This shows the last time data was refreshed.</p>
  //               </TooltipContent>
  //             </Tooltip>
  //           </TooltipProvider>
  //           <span>
  //             Last updated {lastUpdated && lastUpdated.toLocaleTimeString()}
  //           </span>
  //         </div>
  //       </div>
  //     </div>
  //     </div>
  //   );
  // }

  function renderBreadcrumbs() {
    const path = location.pathname.split("?")[0];
    const segments = path.split("/").filter(Boolean);
    console.log("Current Path Segments:", segments);

    const moduleName = localStorage.getItem("smart360_current_module");
    const fullPath = `/${segments.join("/")}`;
    let match = findClosestNavMatch(fullPath);

    // ---------------------------------------------------------
    // FALLBACK MATCHING (for deep paths like /create, /edit, /ID)
    // ---------------------------------------------------------
    if (!match && segments.length > 1) {
      // Step 1: Try with parent path
      const parentSegments = [...segments];
      parentSegments.pop();
      const parentPath = `/${parentSegments.join("/")}`;
      match = findClosestNavMatch(parentPath);

      // Step 2: Final fallback - match by first segment only (e.g., /plan-tarrif)
      if (!match && segments.length > 1) {
        const firstSegmentPath = `/${segments[0]}`;
        match = findClosestNavMatch(firstSegmentPath);
      }
    }

    const matchedItem = match?.item || null;
    const matchedGroup = match?.group || null;

    // ---------------------------------------------------------
    // SPECIAL SEGMENTS DETECTION (create / view / update)
    // ---------------------------------------------------------
    const allowedLastSegments = [
      "create",
      "add",
      "view",
      "update",
      "edit",
      "details",
      "detail",
      "configure",
    ];

    const tabSegments = ["current", "history","plans","tariff"];

    // Find from end → nearest special keyword
    const specialIndex = [...segments]
      .reverse()
      .findIndex((seg) => allowedLastSegments.includes(seg));

    const specialSegment =
      specialIndex !== -1 ? segments[segments.length - 1 - specialIndex] : null;

    const specialSegmentLabel = specialSegment
      ? specialSegment.charAt(0).toUpperCase() + specialSegment.slice(1)
      : null;

    // ---------------------------------------------------------
    // TAB SEGMENT DETECTION (current / history)
    // ---------------------------------------------------------
    const tabSegment = segments.find((seg) => tabSegments.includes(seg));
    const tabSegmentLabel = tabSegment
      ? tabSegment.charAt(0).toUpperCase() + tabSegment.slice(1)
      : null;

    // Determine LAST EFFECTIVE SEGMENT for ID detection
    // If we have a special segment or a tab segment at the end, that's our anchor
    const effectiveLastSegment = segments[segments.length - 1];

    // ---------------------------------------------------------
    // DYNAMIC ID DETECTION
    // ---------------------------------------------------------
    let dynamicId: string | null = null;

    // An ID is a segment that is:
    // 1. Not a module (first segment)
    // 2. Not in the menu
    // 3. Not a special action (create/edit etc)
    // 4. Not a tab segment (current/history)
    const isInMenu = (seg: string) =>
      navigationItems.some((group) =>
        group.items.some((item) =>
          normalize(item.link).split("/").includes(seg),
        ),
      );

    const isSpecial = (seg: string) => allowedLastSegments.includes(seg);
    const isTab = (seg: string) => tabSegments.includes(seg);

    // Look for a segment that looks like an ID
    const potentialId = segments.find(
      (seg, idx) =>
        idx > 0 &&
        !isInMenu(seg) &&
        !isSpecial(seg) &&
        !isTab(seg) &&
        /^[A-Za-z0-9-_]+$/.test(seg),
    );

    dynamicId = potentialId || null;

    // ---------------------------------------------------------
    // BUILD BREADCRUMBS
    // ---------------------------------------------------------
    const breadcrumbs: JSX.Element[] = [];

    // MODULE
    const firstModuleRoute = navigationItems[0]?.items?.[0]?.link;
    breadcrumbs.push(
      <BreadcrumbItem key="module">
        <BreadcrumbLink asChild>
          <Link to={firstModuleRoute}>{moduleName?.toUpperCase()}</Link>
        </BreadcrumbLink>
      </BreadcrumbItem>,
    );

    if (matchedGroup || matchedItem || specialSegment || tabSegment || dynamicId) {
      breadcrumbs.push(<BreadcrumbSeparator key="sep-module" />);
    }

    // GROUP
    if (matchedGroup) {
      const firstGroupItem = matchedGroup.items[0];

      breadcrumbs.push(
        <BreadcrumbItem key="group">
          <BreadcrumbLink asChild>
            <Link to={firstGroupItem.link}>{matchedGroup.label}</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>,
      );

      if (matchedItem || specialSegment || tabSegment) {
        breadcrumbs.push(<BreadcrumbSeparator key="sep-group" />);
      }
    }

    // COMPONENT
    if (matchedItem) {
      breadcrumbs.push(
        <BreadcrumbItem key="component">
          {dynamicId || specialSegment || tabSegment ? (
            <BreadcrumbLink asChild>
              <Link to={matchedItem.link}>{matchedItem.label}</Link>
            </BreadcrumbLink>
          ) : (
            <BreadcrumbPage>{matchedItem.label}</BreadcrumbPage>
          )}
        </BreadcrumbItem>,
      );

      if (specialSegment || tabSegment) {
        breadcrumbs.push(<BreadcrumbSeparator key="sep-component" />);
      }
    }

    // TAB SEGMENT (current / history)
    if (tabSegment) {
      breadcrumbs.push(
        <BreadcrumbItem key="tab">
          {dynamicId || specialSegment ? (
            <BreadcrumbLink asChild>
              <Link to={`${matchedItem?.link || ""}?tab=${tabSegment}`}>
                {tabSegmentLabel}
              </Link>
            </BreadcrumbLink>
          ) : (
            <BreadcrumbPage>{tabSegmentLabel}</BreadcrumbPage>
          )}
        </BreadcrumbItem>,
      );

      if (specialSegment) {
        breadcrumbs.push(<BreadcrumbSeparator key="sep-tab" />);
      }
    }

    // Special Last Segment (create / view / update)
    if (specialSegment) {
      breadcrumbs.push(
        <BreadcrumbItem key="special">
          <BreadcrumbPage>{specialSegmentLabel}</BreadcrumbPage>
        </BreadcrumbItem>,
      );
    }

    // ---------------------------------------------------------
    // FINAL RENDER
    // ---------------------------------------------------------
    return (
      <div>
        {envConfig.show && (
          <div
            className={`flex items-center gap-2 border ${envConfig.borderColor} ${envConfig.bgColor} p-2 rounded`}
          >
            <InfoIcon className={`w-4 h-4 ${envConfig.iconColor}`} />
            <span className={envConfig.textColor}>{envConfig.label}</span>
          </div>
        )}

        <div className="flex items-center justify-between w-full">
          <Breadcrumb>
            <BreadcrumbList>{breadcrumbs}</BreadcrumbList>
          </Breadcrumb>

          <div className="flex flex-col items-end gap-2">
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-2 py-2 mt-2 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-1 text-gray-600 text-xs">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 cursor-pointer hover:text-gray-800 transition-colors" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This shows the last time data was refreshed.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <span>
                Last updated {lastUpdated && lastUpdated.toLocaleTimeString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return {
    error,
    setError,
    renderError,
    renderBreadcrumbs,
  };
}
